import exp from "constants";
import { PythonSVG, GoSVG, PipSVG, CSSSVG, DockerSVG, GitSVG, GraphQLSVG, HTMLSVG, JavaSVG, JavascriptSVG, JestSVG, NestSVG, NodeJsSVG, NpmSVG, ReactSVG, RightArrowSVG, SQLSVG, SpringbootSVG, TypescriptSVG, PoetrySVG, ShellSVG } from "../../assets/iconSvg";
import HeadLayout from "../../components/layout/RootLayout";
import './style.css'
import React, { useEffect, useState } from "react";

interface Skill {
  icon: any;
  title: string;
  rating: string;
  bar: string
}

interface StudyEntity {
  nombre?: string;
  institucion?: string;
  fecha?: string;
  duracion?: string;
  logo?: string;
  certificado?: string;
  descripcion?: string;
}

interface JobExperience {
  job: string;
  company: string;
  dates: string;
  description?: string | null;
}

const SectionHeader = ({ title, isCollapsed, onToggle }: { title: string, isCollapsed: boolean, onToggle: () => void }) => {
  return (
    <div className="relative mt-6 sm:mt-8 cursor-pointer transition-opacity" onClick={onToggle}>
      <div className="flex items-center">
        <div className="relative px-3 sm:px-4 md:px-6 py-2 bg-white dark:bg-[#1E293B] rounded-full z-20 transform -translate-y-1/2 max-w-[90%] overflow-visible">
          <h2 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-blue-600 dark:text-blue-400 break-words hyphens-auto overflow-visible whitespace-normal word-break-break-word">
            {title}
          </h2>
        </div>
      </div>
    </div>
  );
};

const SkillCategory = ({ skills, main, isCollapsed, onToggle }: { skills: Skill[], main: string, isCollapsed: boolean, onToggle: () => void }) => {
  return (
    <div className="bg-white dark:bg-transparent">
      <SectionHeader title={main} isCollapsed={isCollapsed} onToggle={onToggle} />
      <div className={`transition-all duration-500 ${isCollapsed ? 'max-h-0 opacity-0 overflow-hidden' : 'max-h-[2000px] opacity-100 overflow-visible'}`}>
        <section className="p-2 sm:p-3 md:p-4 lg:p-5 rounded-xl dark:shadow-colorDa/20 dark:bg-[#1E293B]/80 shadow-gray-200 bg-white overflow-visible">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 md:gap-4 lg:gap-5 overflow-visible pb-2">
            {skills.map((skill: Skill, index: number) => (
              <div 
                key={index}
                className="bg-white dark:bg-[#38465E] rounded-lg shadow-md hover:shadow-lg transition-all duration-300 hover:translate-y-[-5px] opacity-0 animate-scaleIn perspective z-10 min-w-[80px] sm:min-w-[100px] md:min-w-[120px] lg:min-w-[160px] min-h-[180px] sm:min-h-[200px] md:min-h-[220px] lg:min-h-[240px] overflow-visible"
                style={{ animationDelay: `${100 * (index % 10)}ms` }}
              >
                <div className="flex flex-col items-center p-2 sm:p-3 md:p-4 lg:p-6 group h-full">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 lg:w-16 lg:h-16 flex items-center justify-center mb-3 group-hover:animate-float">
                    {skill.icon}
                  </div>
                  <div className="w-full text-center">
                    <p className="font-medium text-[10px] sm:text-xs md:text-sm lg:text-base mb-2 dark:text-colorLb text-gray-800 break-words hyphens-auto overflow-visible word-break-break-word px-1">
                      {skill.title}
                    </p>
                    <div className="w-full bg-gray-200 rounded-full h-1.5 sm:h-2 md:h-2.5 lg:h-3 mb-1 dark:bg-gray-700 overflow-hidden">
                      <div 
                        className={`h-1.5 sm:h-2 md:h-2.5 lg:h-3 rounded-full ${
                          skill.bar === "green" ? "bg-green-500" : 
                          skill.bar === "blue" ? "bg-blue-500" : 
                          skill.bar === "orange" ? "bg-orange-500" : "bg-red-500"
                        } transition-all duration-1000 transform origin-left scale-x-100`} 
                        style={{ width: `${parseFloat(skill.rating) * 10}%` }}
                      ></div>
                    </div>
                    <span className="text-[10px] sm:text-xs md:text-sm lg:text-base text-gray-500 dark:text-gray-400 group-hover:text-gray-800 dark:group-hover:text-gray-200 transition-colors font-medium">
                      {skill.rating}/10
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

const ExperienceCategory = ({ experiences, main, isCollapsed, onToggle }: { experiences: JobExperience[], main: string, isCollapsed: boolean, onToggle: () => void }) => {
  return (
    <div className="bg-white dark:bg-transparent">
      <SectionHeader title={main} isCollapsed={isCollapsed} onToggle={onToggle} />
      <div className={`transition-all duration-500 ${isCollapsed ? 'max-h-0 opacity-0 overflow-hidden' : 'max-h-[2000px] opacity-100 overflow-visible'}`}>
        <section className="p-2 sm:p-3 md:p-4 lg:p-5 rounded-xl dark:shadow-colorDa/20 dark:bg-[#1E293B]/80 shadow-gray-200 bg-white overflow-visible">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3 md:gap-4 lg:gap-5 overflow-visible pb-2">
            {experiences.map((experience: JobExperience, index: number) => (
              <article
                key={index}
                className={`flex flex-col content-center w-full rounded-xl 
                dark:bg-[#384457] shadow-lg p-2 sm:p-3 md:p-4 lg:p-5 my-2 text-colorDa relative group overflow-visible transition-all duration-300 
                hover:shadow-xl hover:scale-105 border-l-4 ${index === 0 ? 'border-green-500 dark:border-green-400' : 'border-blue-500 dark:border-blue-400'}
                opacity-0 animate-fadeInRight z-10 min-h-[200px] max-w-full`}
              >
                {index === 0 && (
                  <div className="absolute top-0 right-0">
                    <span className="inline-block bg-green-500 text-white text-xs px-2 py-1 rounded-bl-lg font-semibold">
                      Current
                    </span>
                  </div>
                )}
                <div className="z-10 flex flex-col h-full w-full overflow-visible">
                  <div className="flex-1 overflow-visible">
                    <h2 className="text-lg sm:text-xl md:text-2xl lg:text-2xl text-left mt-1 font-bold dark:text-colorLb break-words hyphens-auto overflow-visible word-break-break-word">{experience.job}</h2>
                    <div className="flex items-center mt-2">
                      <span className="text-sm sm:text-base md:text-lg lg:text-lg text-[#f84525] dark:text-[#ff7043] font-semibold break-words hyphens-auto word-break-break-word">{experience.company}</span>
                    </div>
                    <p className="dark:text-colorLa text-left mt-3 text-xs sm:text-sm md:text-base lg:text-base opacity-80 break-words hyphens-auto whitespace-normal overflow-visible word-break-break-word">{experience.description}</p>
                  </div>
                  <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                    <p className="text-left dark:text-colorLa text-xs sm:text-sm font-medium break-words hyphens-auto word-break-break-word">
                      {index === 0 ? 
                        <span className="flex items-center">
                          {experience.dates}
                        </span> : 
                        experience.dates
                      }
                    </p>
                  </div>
                </div>
                <div className={`absolute inset-0 bg-gradient-to-br ${index === 0 ? 'from-green-500/5 to-white/10' : 'from-blue-500/5 to-white/10'} dark:from-white dark:to-white opacity-0 group-hover:opacity-10 transition-opacity duration-500 z-0`}></div>
              </article>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

const EducationCategory = ({ educationData, main, isCollapsed, onToggle }: { educationData: any[], main: string, isCollapsed: boolean, onToggle: () => void }) => {
  return (
    <div className="bg-white dark:bg-transparent">
      <SectionHeader title={main} isCollapsed={isCollapsed} onToggle={onToggle} />
      <div className={`transition-all duration-500 ${isCollapsed ? 'max-h-0 opacity-0 overflow-hidden' : 'max-h-[2000px] opacity-100 overflow-visible'}`}>
        <section className="p-2 sm:p-3 md:p-4 lg:p-5 rounded-xl dark:shadow-colorDa/20 dark:bg-[#1E293B]/80 shadow-gray-200 bg-white overflow-visible">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-3 md:gap-4 lg:gap-5 overflow-visible pb-2">
            {educationData.map((item, index) => (
              <div 
                key={index} 
                className="relative group perspective overflow-hidden rounded-xl bg-white dark:bg-[#38465E] shadow-md hover:shadow-lg transition-all duration-500 hover:translate-y-[-5px] border border-gray-100 dark:border-gray-700 opacity-0 animate-fadeInUp z-10 min-h-[280px] my-2"
                style={{ animationDelay: `${100 * (index % 12)}ms` }}
              >
                <div 
                  aria-hidden="true" 
                  className="absolute inset-0 bg-gradient-to-b from-blue-500/20 to-transparent dark:from-blue-400/10 dark:to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-xl"
                ></div>
                
                <div className="relative p-4 sm:p-6 md:p-8 h-full flex flex-col">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center mb-4 group-hover:translate-y-[-5px] transition-transform duration-300">
                    <div className="flex-shrink-0 h-16 w-16 sm:h-20 sm:w-20 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden flex items-center justify-center shadow-md group-hover:shadow-lg transition-all duration-300 mb-3 sm:mb-0">
                      <img 
                        className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-300" 
                        src={item.logo} 
                        alt={item.institution} 
                        loading="lazy" 
                      />
                    </div>
                    <div className="sm:ml-5 flex-1">
                      <h3 className="text-lg sm:text-xl font-bold dark:text-colorLa text-gray-800 group-hover:text-[#0071c2] dark:group-hover:text-blue-300 transition-colors duration-300 break-words">{item.institution}</h3>
                      <h4 className="text-base sm:text-lg font-medium dark:text-colorLb text-gray-600 mt-1 group-hover:text-gray-800 dark:group-hover:text-gray-200 transition-colors duration-300 break-words">{item.title}</h4>
                    </div>
                  </div>
                  
                  <div className="mb-4 sm:mb-6 flex-grow">
                    <p className="text-gray-700 dark:text-gray-300 text-sm sm:text-base line-clamp-4 group-hover:line-clamp-none transition-all duration-300 break-words">{item.description}</p>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center pt-3 border-t border-gray-200 dark:border-gray-700 mt-auto gap-2 sm:gap-0">
                    <span className="text-sm text-gray-500 dark:text-gray-400 flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="truncate">{item.date}</span>
                    </span>
                    
                    {item.certificate ? (
                      <a 
                        href={`certificates/${item.certificate}`} 
                        target="_blank" 
                        rel="noreferrer"
                        className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium transition-all duration-300 hover:translate-x-1" 
                      >
                        <span>Certificate</span>
                        <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24" className="ml-1 transform group-hover:translate-x-1 transition-transform duration-300 flex-shrink-0">
                          <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m17 13l-5 5m0 0l-5-5m5 5V6"></path>
                        </svg>
                      </a>
                    ) : (
                      <span className="text-sm text-gray-400 dark:text-gray-500">In Progress</span>
                    )}
                  </div>
                </div>
                
                {/* Decorative corner */}
                <div className="absolute top-0 right-0 w-16 h-16 transform translate-x-8 -translate-y-8 rotate-45 bg-blue-500/10 dark:bg-blue-400/10 group-hover:translate-x-4 group-hover:-translate-y-4 transition-transform duration-500"></div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

const EducationChildren = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const educationData = [
    {
      title: "Software Engineer",
      institution: "Universidad de Buenos Aires",
      date: "2018 / Present",
      description: "University Degree with focus on software development, algorithms, and data structures.",
      logo: "logos/uba.png",
      certificate: undefined
    },
    {
      title: "React.js",
      institution: "Coderhouse",
      date: "2022",
      description: "ECMA6, DOM, JSX, Transpiling, Components, Promises, Async, MAP, API, Routing, Events, Context, Rendering, Firebase",
      logo: "logos/coder.jpg",
      certificate: "coder-react.png"
    },
    {
      title: "RU102JS: Redis for JavaScript Developers",
      institution: "Redis",
      date: "2022",
      description: "API, Promises, Cluster, Libraries, RESP, Javascript, Binary Data, Performance Optimization",
      logo: "logos/redis.jpeg",
      certificate: "redis.png"
    },
    {
      title: "Google Data Analytics",
      institution: "Google / Coursera",
      date: "2022",
      description: "Data Analysis, Creating case studies, Data Visualization, Data Integrity, Data Cleansing, Data Collection, Spreadsheet, SQL, Data Ethics, Aggregation, R Markdown, R Programming, Rstudio, Tableau Software",
      logo: "logos/google.jpeg",
      certificate: "data-analytics.jpeg"
    },
    {
      title: "Java Fullstack Springboot",
      institution: "Egg Live",
      date: "2021 / 2022",
      description: "Java, Collections, Classes, Encapsulation, Polymorphism, Overloading, Methods, JDBC, JPA, Spring, Maven",
      logo: "logos/egg.jpeg",
      certificate: "java-egg.pdf"
    },
    {
      title: "Fullstack Node.js",
      institution: "Digital House",
      date: "2019 / 2020",
      description: "Node.js, Express, React, MongoDB, MySQL, Javascript ES6-8, MVC Architecture",
      logo: "logos/digitalhouse.jpeg",
      certificate: "digitalhouse.pdf"
    }
  ];

  return (
    <section id="education" className="relative z-10 overflow-visible mb-16">
      <EducationCategory 
        educationData={educationData} 
        main="<Education />" 
        isCollapsed={isCollapsed} 
        onToggle={() => setIsCollapsed(!isCollapsed)} 
      />
    </section>
  )
};

const SkillsChildren = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const skills: Skill[] = [
    {
      icon: <JavascriptSVG size="24" className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8" />,
      title: "Javascript",
      rating: "9.3",
      bar: "green",
    },
    {
      icon: <GitSVG size="24" className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8" />,
      title: "Git",
      rating: "9",
      bar: "green",
    },
    {
      icon: <PythonSVG size="24" className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8" />,
      title: "Python",
      rating: "8.7",
      bar: "green",
    },
    {
      icon: <NestSVG size="24" className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8" />,
      title: "Nest",
      rating: "8.7",
      bar: "green",
    },
    {
      icon: <img src="/logos/fury.png" alt="Fury" className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 object-contain" />,
      title: "Fury",
      rating: "8.5",
      bar: "green",
    },
    {
      icon: <HTMLSVG size="24" className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8" />,
      title: "HTML",
      rating: "8.5",
      bar: "green",
    },
    {
      icon: <ShellSVG size="24" className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8" color="#4EAA25" />,
      title: "Shell",
      rating: "8.4",
      bar: "green",
    },
    {
      icon: <NodeJsSVG size="24" className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8" />,
      title: "Node.js",
      rating: "8.4",
      bar: "green",
    },
    {
      icon: <TypescriptSVG size="24" className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8" />,
      title: "Typescript",
      rating: "8.3",
      bar: "green",
    },
    {
      icon: <GraphQLSVG size="24" className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8" />,
      title: "GraphQL",
      rating: "8.3",
      bar: "green",
    },
    {
      icon: <PoetrySVG size="24" className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8" />,
      title: "Poetry",
      rating: "7.9",
      bar: "blue",
    },
    {
      icon: <PipSVG size="24" className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8" />,
      title: "Pip",
      rating: "7.8",
      bar: "blue",
    },
    {
      icon: <JestSVG size="24" className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8" />,
      title: "Jest",
      rating: "7.4",
      bar: "blue",
    },
    {
      icon: <SQLSVG size="24" className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8" color='#1C274C' />,
      title: "SQL",
      rating: "7.4",
      bar: "blue",
    },
    {
      icon: <NpmSVG size="24" className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8" />,
      title: "NPM",
      rating: "7.3",
      bar: "blue",
    },
    {
      icon: <img src="/logos/flask.png" alt="Flask" className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 object-contain" />,
      title: "Flask",
      rating: "7.2",
      bar: "blue",
    },
    {
      icon: <CSSSVG size="24" className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8" />,
      title: "CSS",
      rating: "7.2",
      bar: "blue",
    },
    {
      icon: <ReactSVG size="24" className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8" />,
      title: "React",
      rating: "7.1",
      bar: "blue",
    },
    {
      icon: <DockerSVG size="24" className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8" color="#1C274C" />,
      title: "Docker",
      rating: "7",
      bar: "blue",
    },
    {
      icon: <GoSVG size="24" className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8" />,
      title: "Go",
      rating: "6.8",
      bar: "blue",
    },
    {
      icon: <SQLSVG size="24" className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8" color='orange' />,
      title: "NoSQL",
      rating: "6.7",
      bar: "orange",
    },
    {
      icon: <JavaSVG size="24" className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8" />,
      title: "Java",
      rating: "6.4",
      bar: "orange",
    },
    {
      icon: <ReactSVG size="24" className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8" color='#800080' />,
      title: "React Native",
      rating: "5.4",
      bar: "orange",
    },
    {
      icon: <SpringbootSVG size="24" className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8" />,
      title: "Springboot",
      rating: "3.9",
      bar: "red",
    },
  ];

  return (
    <section id="skills" className="relative z-10 overflow-visible mb-16">
      <SkillCategory 
        skills={skills} 
        main="<Skills />" 
        isCollapsed={isCollapsed} 
        onToggle={() => setIsCollapsed(!isCollapsed)} 
      />
    </section>
  )
};

const ExperienceChildren = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  const experiences: JobExperience[] =[
    {
      job: "Software Developer",
      company: "MercadoLibre",
      dates: "Aug 2024 - Current",
      description: "Fury · Python · Poetry · Palo Alto · VPN · Workspace One · RDS · AD · Jira · MySQL · BigQuery · Looker · Microservices · Arduino"
    },
    {
      job: "Jr. Backend Developer",
      company: "Nimblelab",
      dates: "Aug 2023 - Feb 2024",
      description: "Docker · Jest · Nest · Sequelize · Next · DOM · CI/CD · AWS · OpenAI · Library Building · CRON"
    },
    {
      job: "Jr. Developer",
      company: "Qonico",
      dates: "Apr 2023 - Jul 2023",
      description: "Astro · React · React Native · Redis · Docker · Sequelize · Typescript · Javascript"
    },
    {
      job: "Teacher assistant",
      company: "Coderhouse",
      dates: "Mar 2023 - Dec 2023",
      description: "Next · Redux · Node · Rest · Back-End Web Development · PWA · Bootstrap ·  Nest · E-Commerce · Git · WebSockets ·  JWT · CRUD · jQuery · JavaScript · React"
    },
    {
      job: "Teacher",
      company: "Argentina School",
      dates: "Sep 2022 - Feb 2023",
      description: "Blockly Programming · HTML · CSS · JavaScript · SQL · MySQL · Arduino · Raspberry"
    },
    {
      job: "Web Developer",
      company: "Freelance",
      dates: "Jan 2021 - Feb 2023",
      description: 'HTML · CSS · Javascript · SQL · Serverless · Git · Vercel'
    },
    {
      job: "Technical Assistance",
      company: "CAT Technologies Argentina",
      dates: "Jun 2022 - Oct 2022",
      description: "IP · Online Support · DNS · Call Center · WiFi · Invoice · Email"
    },
    {
      job: "Help Desk",
      company: "Family Company",
      dates: "Mar 2014 - Jan 2020",
      description: 'Word · Excel · Powerpoint · Remote Support · Catching customers'
    },
    {
      job: "Assistant Manager",
      company: "GrupoGBK",
      dates: "Apr 2016 - Oct 2018",
      description: 'Photoshop · Illustrator · Excel · Word · Paint · Blender · Thinkercad'
    }
  ]
  
  return (
    <section id="experience" className="relative z-10 overflow-visible mb-16">
      <ExperienceCategory 
        experiences={experiences} 
        main="<Experience />" 
        isCollapsed={isCollapsed} 
        onToggle={() => setIsCollapsed(!isCollapsed)} 
      />
    </section>
  )
};

const ContactCategory = ({ main, isCollapsed, onToggle }: { main: string, isCollapsed: boolean, onToggle: () => void }) => {
  return (
    <div className="bg-white dark:bg-transparent">
      <SectionHeader title={main} isCollapsed={isCollapsed} onToggle={onToggle} />
      <div className={`transition-all duration-500 ${isCollapsed ? 'max-h-0 opacity-0 overflow-hidden' : 'max-h-[2000px] opacity-100 overflow-visible'}`}>
        <div className="p-4 sm:p-6 md:p-8 rounded-xl dark:shadow-colorDa/20 dark:bg-[#1E293B]/80 shadow-gray-200 bg-white">
          <div className="max-w-3xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              <div>
                <h3 className="text-lg sm:text-xl font-bold mb-3 text-gray-800 dark:text-gray-200">Get In Touch</h3>
                <p className="mb-4 dark:text-gray-300 text-gray-600 text-sm sm:text-base text-justify">
                  I'm currently available for freelance opportunities and full-time positions. 
                  If you have a project that needs coding skills, lets get in touch!
                </p>
                
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-center group">
                    <div className="w-8 sm:w-10 h-8 sm:h-10 rounded-full bg-[#0071c2]/10 flex items-center justify-center mr-3 group-hover:bg-[#0071c2]/20 transition-colors duration-300 flex-shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5 text-[#0071c2]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="transform group-hover:translate-x-1 transition-transform duration-300 overflow-hidden">
                      <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Email</p>
                      <a href="mailto:leocapone@hotmail.com" className="text-[#0071c2] dark:text-[#2196f3] font-medium group-hover:underline text-sm sm:text-base truncate block">
                        leocapone@hotmail.com
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-center group">
                    <div className="w-8 sm:w-10 h-8 sm:h-10 rounded-full bg-[#f84525]/10 flex items-center justify-center mr-3 group-hover:bg-[#f84525]/20 transition-colors duration-300 flex-shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5 text-[#f84525]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div className="transform group-hover:translate-x-1 transition-transform duration-300">
                      <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Location</p>
                      <p className="text-gray-800 dark:text-gray-200 font-medium text-sm sm:text-base">
                        Buenos Aires, Argentina
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg sm:text-xl font-bold mb-3 text-gray-800 dark:text-gray-200">Social Profiles</h3>
                <p className="mb-4 dark:text-gray-300 text-gray-600 text-sm sm:text-base">
                  Connect with me on social media and professional networks.
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <a 
                    href="https://github.com/" 
                    target="_blank" 
                    rel="noreferrer"
                    className="flex items-center p-2 sm:p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-300 hover:translate-y-[-3px] hover:shadow-md group"
                  >
                    <span className="w-6 sm:w-8 h-6 sm:h-8 mr-2 sm:mr-3 flex items-center justify-center text-gray-700 dark:text-gray-300 group-hover:text-black dark:group-hover:text-white transition-colors flex-shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                      </svg>
                    </span>
                    <span className="text-sm sm:text-base text-gray-800 dark:text-gray-200 font-medium group-hover:text-black dark:group-hover:text-white transition-colors truncate">GitHub</span>
                  </a>
                  
                  <a 
                    href="https://linkedin.com/" 
                    target="_blank" 
                    rel="noreferrer"
                    className="flex items-center p-2 sm:p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-300 hover:translate-y-[-3px] hover:shadow-md group"
                  >
                    <span className="w-6 sm:w-8 h-6 sm:h-8 mr-2 sm:mr-3 flex items-center justify-center text-[#0A66C2] flex-shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                      </svg>
                    </span>
                    <span className="text-sm sm:text-base text-gray-800 dark:text-gray-200 font-medium group-hover:text-black dark:group-hover:text-white transition-colors truncate">LinkedIn</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ContactChildren = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  return (
    <section id="contact" className="relative z-10 overflow-visible mb-12">
      <ContactCategory 
        main="<Contact />" 
        isCollapsed={isCollapsed} 
        onToggle={() => setIsCollapsed(!isCollapsed)} 
      />
    </section>
  );
};

const pageChildren = () => {
  // Function to scroll to the next section smoothly
  const scrollToContent = () => {
    const contentSection = document.getElementById('content-section');
    if (contentSection) {
      contentSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="flex flex-col w-full overflow-visible">
      {/* Full-page Hero Section */}
      <section className="min-h-screen w-full flex items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#e6f2ff] to-white dark:from-[#0c2d48] dark:to-[#051c30]"></div>
        
        {/* Subtle grid pattern overlay */}
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `linear-gradient(rgba(0,113,194,0.1) 1px, transparent 1px), linear-gradient(to right, rgba(0,113,194,0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
          }}
        ></div>
        
        {/* Background animated elements with static colors */}
        <div className="absolute inset-0 pointer-events-none z-[1]">
          <div className="absolute top-20 left-[15%] w-24 h-24 rounded-full bg-blue-500/10 dark:bg-blue-400/10 backdrop-blur-sm"></div>
          <div className="absolute bottom-32 right-[10%] w-32 h-32 rounded-full bg-blue-500/10 dark:bg-blue-400/10 backdrop-blur-sm"></div>
          <div className="absolute top-1/3 right-[20%] w-20 h-20 rounded-full bg-blue-500/10 dark:bg-blue-400/10 backdrop-blur-sm"></div>
          <div className="absolute bottom-40 left-[25%] w-16 h-16 rounded-full bg-blue-500/10 dark:bg-blue-400/10 backdrop-blur-sm"></div>
          
          {/* Additional geometric elements */}
          <div className="absolute top-1/4 right-1/3 w-12 h-12 bg-blue-500/10 dark:bg-blue-400/10 rounded-md rotate-45 backdrop-blur-sm"></div>
          <div className="absolute bottom-1/4 left-1/3 w-16 h-16 border-2 border-blue-500/20 dark:border-blue-400/20 rounded-full"></div>
        </div>

        <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center text-center relative z-10">
          <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold mb-6 opacity-0 animate-fadeInDown break-words hyphens-auto">
            <span className="relative inline-block before:absolute before:-inset-1 before:block before:-skew-y-3 before:bg-colorLc dark:before:bg-colorDa">
              <span className="relative text-colorDa dark:text-colorLb">Hi! I'm Leonardo Capone.</span>
            </span>
          </h1>
          
          <div className="inline-block bg-[#f84525] text-white px-3 sm:px-4 py-1 sm:py-2 rounded-md text-lg sm:text-xl font-medium mb-4 opacity-0 animate-fadeInUp delay-300 hover:scale-105 transition-transform">
            Full Stack Developer
          </div>
          
          <p className="text-base sm:text-xl max-w-2xl mb-6 sm:text-gray-800 dark:text-gray-200 opacity-0 animate-fadeInUp delay-500 break-words hyphens-auto px-2">
            + {new Date().getFullYear() - 2021} years of experience working with innovative products and creating high-end solutions
          </p>
          
          <div className="flex flex-col items-center">
            <a 
              href={`certificates/cv.pdf`} 
              target="_blank" 
              rel="noreferrer"
              className="bg-[#0071c2] hover:bg-[#0062a8] text-white font-medium py-2 px-6 rounded-md transition-all duration-300 inline-flex items-center gap-2 hover:translate-y-[-5px] hover:shadow-lg opacity-0 animate-fadeInUp delay-700 mb-8"
            >
              <span>Know me better</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 7h10v10"></path><path d="M7 17L17 7"></path>
              </svg>
            </a>
            
            <button 
              onClick={scrollToContent}
              className="animate-arrowBounce text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors focus:outline-none cursor-pointer"
              aria-label="Scroll down"
            >
              <div className="flex flex-col items-center">
                <span className="text-sm font-medium mb-2 tracking-wide opacity-0 animate-fadeInUp delay-900">Scroll Down</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="2.5em" height="2.5em" viewBox="0 0 24 24" className="opacity-0 animate-fadeInUp delay-1000">
                  <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m17 13l-5 5m0 0l-5-5m5 5V6"></path>
                </svg>
              </div>
            </button>
          </div>
        </div>
      </section>

      {/* Content sections with continuous flow */}
      <div id="content-section" className="mx-auto px-4 sm:px-6 lg:px-8 py-12 w-full relative z-10 overflow-visible">
        {/* Subtle background decoration */}
        <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 left-0 w-full h-96 bg-gradient-to-b from-blue-100/50 to-transparent dark:from-blue-900/30 dark:to-transparent blur-xl"></div>
          
          <div className="absolute top-1/3 -right-24 w-64 h-64 rounded-full border border-blue-300/20 dark:border-blue-500/10"></div>
          <div className="absolute top-2/3 -left-32 w-80 h-80 rounded-full border border-blue-300/20 dark:border-blue-500/10"></div>
          
          <div className="absolute bottom-1/4 right-1/4 w-48 h-48 rounded-full bg-blue-300/5 dark:bg-blue-500/5 blur-2xl"></div>
          <div className="absolute top-1/2 left-1/6 w-64 h-64 rounded-full bg-blue-300/5 dark:bg-blue-500/5 blur-xl"></div>
        </div>

        {/* Experience Section */}
        <section className=" reveal">
          {ExperienceChildren()}
        </section>
        
        {/* Education Section */}
        <section className=" reveal">
          {EducationChildren()}
        </section>

        {/* Skills Section */}
        <section className=" reveal">
          {SkillsChildren()}
        </section>
        
        {/* Contact Section */}
        <section className="reveal">
          {ContactChildren()}
        </section>
      </div>
    </div>
  );
};

function Index() {
  // Initialize scroll reveal effect
  useEffect(() => {
    const handleScroll = () => {
      const reveals = document.querySelectorAll('.reveal');
      
      reveals.forEach((reveal) => {
        const revealTop = reveal.getBoundingClientRect().top;
        const windowHeight = window.innerHeight;
        const revealPoint = 150;
        
        if(revealTop < windowHeight - revealPoint) {
          reveal.classList.add('active');
        }
      });
    };
    
    window.addEventListener('scroll', handleScroll);
    // Initial check
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  return (
    <HeadLayout 
      title="Leonardo Capone | Software Developer" 
      description="Software Developer with expertise in Backend, Node.js, TypeScript, and more. Currently working at MercadoLibre." 
      keywords="Leonardo Capone, Software Developer, Backend Developer, MercadoLibre, Node.js, TypeScript, React"
    >
      {pageChildren()}
    </HeadLayout>
  );
}

export default Index;
